import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiActivity, FiAlertTriangle, FiCopy, FiCreditCard, FiRefreshCw, FiUserPlus,
} from 'react-icons/fi';
import { useApi } from '../../useApi';
import {
  PLATFORM_LABELS, appWebsiteSplit, describeDeviceUse, describeTimelineItem, formatWhen, storeSplit,
} from './adminFormat';
import './AdminInsights.css';

/* System admin only (the endpoints are @system_admin_required). Who uses the
   app, on what, and where subscriptions stand — services/admin_insights.py. */

const WINDOWS = [7, 30, 90];
const SUBSCRIBED = ['trial', 'active', 'cancelled', 'billing_issue'];
const AT_RISK = ['cancelled', 'billing_issue'];
// Devices with an app of their own — "hasn't used the app" means nothing elsewhere.
const APP_NAMES = { android: 'Android', apple_mobile: 'iOS' };

const sumStates = (subs, keys) =>
  subs.filter((s) => keys.includes(s.key)).reduce((n, s) => n + s.count, 0);

/* A meter per row: one hue, magnitude only, with the figure printed beside it,
   so nothing has to be read off a colour. */
const BarRows = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  // Every row is its own grid, so the figure column must be ONE width for the
  // whole list — sized to the longest figure — or a "7" row gets a longer
  // track than a "1022" row and the bars stop sharing a scale.
  const valueWidth = `${Math.max(3, String(max).length) + 0.5}ch`;
  return (
    <ul className="ai-bars" style={{ '--ai-value-w': valueWidth }}>
      {rows.map((r) => (
        <li key={r.key} className="ai-bar-row" title={`${r.label}: ${r.value}`}>
          <span className="ai-bar-label">{r.label}</span>
          <span className="ai-bar-track">
            {r.value > 0 && (
              <span className="ai-bar-fill" style={{ width: `${(r.value / max) * 100}%` }} />
            )}
          </span>
          <span className="ai-bar-value">{r.value}</span>
          {r.note && <span className="ai-bar-note">{r.note}</span>}
        </li>
      ))}
    </ul>
  );
};

const StatTile = ({ icon, value, label }) => (
  <div className="admin-stat-card">
    {icon}
    <div className="stat-info">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  </div>
);

export default function AdminInsights() {
  const { authenticatedFetch } = useApi();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [device, setDevice] = useState('');       // '' = everyone
  const [notOnApp, setNotOnApp] = useState(false);
  const [people, setPeople] = useState(null);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const peopleRequest = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authenticatedFetch(`/admin/insights?days=${days}`);
      if (!res.ok) {
        throw new Error(res.status === 403 ? 'System admins only.' : 'Could not load insights.');
      }
      setData(await res.json());
    } catch (err) {
      // A failed request is not an answer: keep what is on screen and say so,
      // rather than drawing a page of zeros.
      setError(err.message || 'Could not load insights.');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, days]);

  useEffect(() => { load(); }, [load]);

  const appFilter = Boolean(APP_NAMES[device]) && notOnApp;

  const loadPeople = useCallback(async (page = 1) => {
    // Filters can change faster than the server answers; only the latest
    // request may write to the list.
    const request = ++peopleRequest.current;
    setPeopleLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), page: String(page) });
      if (device) params.set('device', device);
      if (appFilter) params.set('not_on_app', '1');
      const res = await authenticatedFetch(`/admin/insights/people?${params}`);
      if (!res.ok) throw new Error('Could not load the people list.');
      const body = await res.json();
      if (request !== peopleRequest.current) return;
      setPeople((prev) => (page > 1 && prev ? { ...body, users: [...prev.users, ...body.users] } : body));
    } catch (err) {
      if (request === peopleRequest.current) setError(err.message || 'Could not load the people list.');
    } finally {
      if (request === peopleRequest.current) setPeopleLoading(false);
    }
  }, [authenticatedFetch, days, device, appFilter]);

  useEffect(() => { loadPeople(1); }, [loadPeople]);

  const copyEmails = async () => {
    const emails = (people?.emails || []).join(', ');
    if (!emails) return;
    try {
      await navigator.clipboard.writeText(emails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in some WebViews; let them copy by hand.
      window.prompt('Copy these emails', emails);
    }
  };

  if (!data) {
    return (
      <div className="ai-page">
        {error ? (
          <div className="ai-error">
            {error} <button className="ai-link" onClick={load}>Try again</button>
          </div>
        ) : (
          <p className="ai-empty">Loading insights…</p>
        )}
      </div>
    );
  }

  const subs = data.subscriptions || [];
  const platformRows = (data.platforms || []).map((p) => ({
    key: p.platform,
    label: PLATFORM_LABELS[p.platform] || p.platform,
    value: p.users,
  }));
  const devices = data.devices || [];
  const anySignIns = platformRows.some((r) => r.value > 0);
  const deviceFilters = devices.filter((d) => d.people > 0 || d.device === device);

  return (
    <div className="ai-page">
      <div className="ai-toolbar">
        <div className="ai-windows" role="group" aria-label="Time window">
          {WINDOWS.map((d) => (
            <button
              key={d}
              className={`ai-window${d === days ? ' active' : ''}`}
              onClick={() => setDays(d)}
              aria-pressed={d === days}
            >
              {d} days
            </button>
          ))}
        </div>
        <button className="ai-btn ai-btn--quiet" onClick={load} disabled={loading}>
          <FiRefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="ai-error">{error}</div>}

      <div className="ai-stats">
        <StatTile icon={<FiActivity size={22} />} value={data.active_users} label={`Active · ${days} days`} />
        <StatTile icon={<FiUserPlus size={22} />} value={data.new_users} label={`New · ${days} days`} />
        <StatTile icon={<FiCreditCard size={22} />} value={sumStates(subs, SUBSCRIBED)} label="Subscribed" />
        <StatTile icon={<FiAlertTriangle size={22} />} value={sumStates(subs, AT_RISK)} label="At risk" />
      </div>

      <section className="ai-card">
        <h3>Subscriptions</h3>
        <p className="ai-card-sub">
          Where every store subscriber stands now, from the app's copy of RevenueCat.
        </p>
        <BarRows
          rows={subs.map((s) => ({
            key: s.key, label: s.label, value: s.count, note: storeSplit(s.by_store),
          }))}
        />
        <p className="ai-footnote">Revenue, conversion and churn are in RevenueCat Charts.</p>
      </section>

      <section className="ai-card">
        <h3>Devices</h3>
        <p className="ai-card-sub">
          People who opened the app in the last {days} days, by the device they used, through
          the app or the website. Someone on two devices counts on both.
        </p>
        {anySignIns ? (
          <>
            <BarRows
              rows={devices.map((d) => ({
                key: d.device, label: d.label, value: d.people, note: appWebsiteSplit(d),
              }))}
            />
            <h4 className="ai-subhead">App or website</h4>
            <BarRows rows={platformRows} />
            <p className="ai-footnote">
              An iPad set to request the desktop site shows as a Mac on the website.
            </p>
          </>
        ) : (
          <p className="ai-empty">
            No sign-ins recorded in this window. History starts from the release that added it.
          </p>
        )}
      </section>

      <section className="ai-card">
        <h3>People</h3>
        <p className="ai-card-sub">
          Everyone who opened the app in the last {days} days and what they used, most recent first.
        </p>
        <div className="ai-filters" role="group" aria-label="Device">
          <button
            className={`ai-window${device === '' ? ' active' : ''}`}
            onClick={() => setDevice('')}
            aria-pressed={device === ''}
          >
            Everyone
          </button>
          {deviceFilters.map((d) => (
            <button
              key={d.device}
              className={`ai-window${device === d.device ? ' active' : ''}`}
              onClick={() => setDevice(d.device)}
              aria-pressed={device === d.device}
            >
              {d.label}
            </button>
          ))}
        </div>
        {APP_NAMES[device] && (
          <label className="ai-check">
            <input type="checkbox" checked={notOnApp} onChange={(e) => setNotOnApp(e.target.checked)} />
            Only people who have never opened the {APP_NAMES[device]} app
          </label>
        )}

        {people ? (
          <>
            <div className="ai-actions">
              <span className="ai-count">
                {people.total} {people.total === 1 ? 'person' : 'people'}
              </span>
              <button className="ai-btn" onClick={copyEmails} disabled={!people.emails?.length}>
                <FiCopy size={14} /> {copied ? 'Copied' : `Copy ${people.emails?.length || 0} emails`}
              </button>
            </div>
            {people.users.length > 0 ? (
              <ul className="ai-list">
                {people.users.map((u) => (
                  <li key={u.user_id}>
                    <span className="ai-list-main">
                      <span className="ai-list-title">{u.display_name || u.email}</span>
                      <span className="ai-list-detail">{u.email}</span>
                      <span className="ai-chips">
                        {u.devices.map((d) => (
                          <span key={d.device} className="ai-chip">{describeDeviceUse(d)}</span>
                        ))}
                      </span>
                    </span>
                    <span className="ai-list-when">{formatWhen(u.last_seen)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ai-empty ai-empty--spaced">Nobody matches in this window.</p>
            )}
            {people.page < people.pages && (
              <button
                className="ai-btn ai-btn--quiet ut-more"
                onClick={() => loadPeople(people.page + 1)}
                disabled={peopleLoading}
              >
                {peopleLoading ? 'Loading…' : `Show more (${people.total - people.users.length})`}
              </button>
            )}
          </>
        ) : (
          <p className="ai-empty">Loading people…</p>
        )}
      </section>

      <section className="ai-card">
        <h3>Phone app builds</h3>
        <p className="ai-card-sub">
          Each person's latest build in the last {days} days. A phone app keeps the web build
          it shipped with until it is updated.
        </p>
        {data.builds?.length ? (
          <div className="ai-table-wrap">
            <table className="ai-table">
              <thead>
                <tr><th>App</th><th>Build</th><th className="ai-num">People</th></tr>
              </thead>
              <tbody>
                {data.builds.map((b) => (
                  <tr key={`${b.platform}-${b.build}`}>
                    <td>{PLATFORM_LABELS[b.platform] || b.platform}</td>
                    <td>{b.build || 'Before build tracking'}</td>
                    <td className="ai-num">{b.users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="ai-empty">No phone app sign-ins in this window.</p>
        )}
      </section>

      <section className="ai-card">
        <h3>Recent subscription events</h3>
        <p className="ai-card-sub">
          Every event RevenueCat sent, including the ones that changed nothing.
        </p>
        {data.recent_events?.length ? (
          <ul className="ai-list">
            {data.recent_events.map((e) => {
              const { title, detail } = describeTimelineItem({ kind: 'subscription', ...e });
              return (
                <li key={e.id}>
                  <span className="ai-list-main">
                    <span className="ai-list-title">{title}</span>
                    <span className="ai-list-detail">
                      {[e.user || 'Unknown user', detail].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="ai-list-when">{formatWhen(e.at)}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="ai-empty">No subscription events recorded yet.</p>
        )}
      </section>
    </div>
  );
}
